import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Users, CheckCircle, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BulkInvoiceDialog = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Sélection, 2: Détails facture, 3: Aperçu
  
  // Données
  const [levels, setLevels] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  
  // Filtres
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  
  // Formulaire facture
  const [invoiceForm, setInvoiceForm] = useState({
    title: '',
    description: '',
    total_amount: '',
    due_date: '',
    periodicity: 'MONTHLY'
  });

  const periodicityOptions = [
    { value: 'MONTHLY', label: 'Mensuelle' },
    { value: 'QUARTERLY', label: 'Trimestrielle' },
    { value: 'BIANNUAL', label: 'Semestrielle' },
    { value: 'ANNUAL', label: 'Annuelle' }
  ];

  useEffect(() => {
    if (open) {
      fetchLevels();
      fetchClasses();
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setStep(1);
    setSelectedLevel('');
    setSelectedClass('');
    setStudents([]);
    setSelectedStudents([]);
    setInvoiceForm({
      title: '',
      description: '',
      total_amount: '',
      due_date: '',
      periodicity: 'MONTHLY'
    });
  };

  const fetchLevels = async () => {
    try {
      const token = localStorage.getItem('access_token');
      // Récupérer les niveaux depuis la collection levels
      const response = await axios.get(`${API}/public/levels`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Transformer les données pour correspondre au format attendu
      const formattedLevels = response.data.map(level => ({
        id: level.level,
        name: level.label
      }));
      setLevels(formattedLevels);
    } catch (error) {
      console.error('Error fetching levels:', error);
      // Si erreur, essayer de récupérer depuis la DB levels directement
      try {
        const token = localStorage.getItem('access_token');
        const levelsResponse = await axios.get(`${BACKEND_URL}/api/levels`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLevels(levelsResponse.data);
      } catch (err) {
        console.error('Error fetching levels from fallback:', err);
      }
    }
  };

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchStudentsByLevel = async (level) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/students/by-level/${level}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students by level:', error);
      toast.error('Erreur lors du chargement des élèves');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentsByClass = async (classId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/classes/${classId}/students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students by class:', error);
      toast.error('Erreur lors du chargement des élèves');
    } finally {
      setLoading(false);
    }
  };

  const handleLevelChange = (level) => {
    setSelectedLevel(level);
    setSelectedClass('');
    setSelectedStudents([]);
    fetchStudentsByLevel(level);
  };

  const handleClassChange = (classId) => {
    setSelectedClass(classId);
    setSelectedLevel('');
    setSelectedStudents([]);
    fetchStudentsByClass(classId);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedStudents(students.map(s => s.id));
    } else {
      setSelectedStudents([]);
    }
  };

  const handleStudentToggle = (studentId, checked) => {
    if (checked) {
      setSelectedStudents([...selectedStudents, studentId]);
    } else {
      setSelectedStudents(selectedStudents.filter(id => id !== studentId));
    }
  };

  const handleNextStep = () => {
    if (step === 1) {
      if (selectedStudents.length === 0) {
        toast.error('Veuillez sélectionner au moins un élève');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!invoiceForm.title || !invoiceForm.total_amount || !invoiceForm.due_date) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }
      setStep(3);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(
        `${API}/invoices/bulk`,
        {
          student_ids: selectedStudents,
          title: invoiceForm.title,
          description: invoiceForm.description,
          total_amount: parseFloat(invoiceForm.total_amount),
          due_date: new Date(invoiceForm.due_date).toISOString(),
          periodicity: invoiceForm.periodicity
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      
      if (response.data.failed_count > 0) {
        toast.warning(`${response.data.failed_count} facture(s) n'ont pas pu être créées`);
      }
      
      onSuccess();
      onClose();
      resetForm();
    } catch (error) {
      console.error('Error creating bulk invoices:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création des factures');
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = selectedStudents.length * parseFloat(invoiceForm.total_amount || 0);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Créer des factures groupées</span>
          </DialogTitle>
          <DialogDescription>
            Créez plusieurs factures en une seule fois pour un groupe d'élèves
          </DialogDescription>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="flex items-center justify-center space-x-4 mb-6">
          <div className={`flex items-center space-x-2 ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
              1
            </div>
            <span className="text-sm font-medium">Sélection</span>
          </div>
          <div className="w-12 h-0.5 bg-gray-300"></div>
          <div className={`flex items-center space-x-2 ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
              2
            </div>
            <span className="text-sm font-medium">Détails</span>
          </div>
          <div className="w-12 h-0.5 bg-gray-300"></div>
          <div className={`flex items-center space-x-2 ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
              3
            </div>
            <span className="text-sm font-medium">Aperçu</span>
          </div>
        </div>

        {/* Step 1: Sélection des élèves */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Filtrer par niveau</Label>
                <Select value={selectedLevel} onValueChange={handleLevelChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un niveau" />
                  </SelectTrigger>
                  <SelectContent>
                    {levels.map((level) => (
                      <SelectItem key={level.id} value={level.id}>
                        {level.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Filtrer par classe</Label>
                <Select value={selectedClass} onValueChange={handleClassChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir une classe" />
                  </SelectTrigger>
                  <SelectContent>
                    {classes.map((cls) => (
                      <SelectItem key={cls.id} value={cls.id}>
                        {cls.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            )}

            {!loading && students.length > 0 && (
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          checked={selectedStudents.length === students.length}
                          onCheckedChange={handleSelectAll}
                        />
                        <Label className="text-sm font-medium">
                          Tout sélectionner ({students.length} élèves)
                        </Label>
                      </div>
                      <Badge variant="outline">
                        {selectedStudents.length} sélectionné(s)
                      </Badge>
                    </div>

                    <div className="border rounded-lg max-h-96 overflow-y-auto">
                      <div className="divide-y">
                        {students.map((student) => (
                          <div key={student.id} className="flex items-center space-x-3 p-3 hover:bg-gray-50">
                            <Checkbox
                              checked={selectedStudents.includes(student.id)}
                              onCheckedChange={(checked) => handleStudentToggle(student.id, checked)}
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium">
                                {student.first_name} {student.last_name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {student.class_name || 'Sans classe'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {!loading && students.length === 0 && (selectedLevel || selectedClass) && (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                <p>Aucun élève trouvé pour cette sélection</p>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Détails de la facture */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Titre de la facture *</Label>
              <Input
                id="title"
                value={invoiceForm.title}
                onChange={(e) => setInvoiceForm({...invoiceForm, title: e.target.value})}
                placeholder="Ex: Frais de scolarité - Janvier 2025"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={invoiceForm.description}
                onChange={(e) => setInvoiceForm({...invoiceForm, description: e.target.value})}
                placeholder="Description optionnelle"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="amount">Montant (GNF) *</Label>
                <Input
                  id="amount"
                  type="number"
                  value={invoiceForm.total_amount}
                  onChange={(e) => setInvoiceForm({...invoiceForm, total_amount: e.target.value})}
                  placeholder="150000"
                />
              </div>

              <div>
                <Label htmlFor="due_date">Date d'échéance *</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={invoiceForm.due_date}
                  onChange={(e) => setInvoiceForm({...invoiceForm, due_date: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="periodicity">Périodicité</Label>
              <Select 
                value={invoiceForm.periodicity} 
                onValueChange={(value) => setInvoiceForm({...invoiceForm, periodicity: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {periodicityOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Step 3: Aperçu */}
        {step === 3 && (
          <div className="space-y-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Titre:</span>
                    <span className="text-sm">{invoiceForm.title}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Montant unitaire:</span>
                    <span className="text-sm">{parseFloat(invoiceForm.total_amount || 0).toLocaleString()} GNF</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Nombre d'élèves:</span>
                    <span className="text-sm font-bold">{selectedStudents.length}</span>
                  </div>
                  <div className="flex items-center justify-between border-t pt-3">
                    <span className="font-bold">Montant total:</span>
                    <span className="text-lg font-bold text-blue-600">
                      {totalAmount.toLocaleString()} GNF
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-900">Notifications automatiques</p>
                  <p className="text-yellow-800 mt-1">
                    Chaque parent recevra une notification pour la nouvelle facture créée.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div>
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                Retour
              </Button>
            )}
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={onClose}>
              Annuler
            </Button>
            {step < 3 ? (
              <Button onClick={handleNextStep}>
                Suivant
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Créer {selectedStudents.length} facture(s)
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkInvoiceDialog;
